import { dehydrate, QueryClient } from '@tanstack/react-query';
import { Promotion as PromotionPage } from 'components/pages/Promotion';
import { fetchPromotion, PROMOTION_PAGE_KEY } from 'hooks/pages/usePromotion';
import { fetchPromotions } from 'hooks/usePromotions';
import { env } from 'lib/constants/env';
import type { PageContext } from 'types/common/PageContext';
import {
  blocksBuildTimeResolver,
  prefetchCommonData,
  prefetchReviewsData,
} from 'utils/buildTime';
import { fetchWithSentry } from 'utils/pageTemplates/fetchWithSentry';

export function promotionPage() {
  async function getStaticProps(context: PageContext) {
    const queryClient = new QueryClient();

    const promotionPageData = await fetchWithSentry({
      queryClient,
      queryKey: [PROMOTION_PAGE_KEY, context.params.slug],
      queryFn: fetchPromotion,
      tags: {
        template: 'promotionPage',
        slug: context.params.slug,
        api: 'sanity',
      },
    });

    if (!promotionPageData) {
      return {
        notFound: true,
        revalidate: env.ISR_TTL_SECONDS,
      };
    }

    await prefetchCommonData(queryClient);
    await prefetchReviewsData(queryClient);
    await blocksBuildTimeResolver(queryClient, promotionPageData.blocks);

    return {
      revalidate: env.ISR_TTL_SECONDS,
      props: {
        dehydratedState: dehydrate(queryClient),
      },
    };
  }

  const getStaticPaths = async () => {
    const promotions = await fetchPromotions();
    const paths = promotions.map((promotion) => ({
      params: { slug: promotion.slug },
    }));

    const pathToRender = paths.find(
      (path) => path.params.slug === env.TEST_PROMOTION_PAGE,
    );

    return { paths: pathToRender ? [pathToRender] : [], fallback: 'blocking' };
  };

  const PromotionPageComponent = () => <PromotionPage />;

  return {
    getStaticProps,
    getStaticPaths,
    PromotionPageComponent,
  };
}
